import { ActionIcon, Tooltip } from '@mantine/core';
import { type CameraControls, Environment } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { variants } from '@uag/react-core';
import { memo, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxHelper, type WebGLRenderer } from 'three';
import { useShallow } from 'zustand/shallow';

import { InsertPointsVisibiltyButton } from 'app/modules/wallforming/components/catalogue-container/article-preview/InsertPointsVisibilityButton';
import { ActionShortcutHint } from 'app/modules/wallforming/components/input-binding/ActionShortcutHint';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { ArticlePreviewScene } from 'app/modules/wallforming/components/scenes/ArticlePreviewScene';
import { usePersistentPreviewCamera } from 'app/modules/wallforming/components/scenes/camera-controls/hooks/usePersistentPreviewCamera';
import { PreviewCameraControls } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewCameraControls';
import { PreviewOrientationCube } from 'app/modules/wallforming/components/scenes/camera-controls/PreviewOrientationCube';
import { SceneLighting } from 'app/modules/wallforming/components/scenes/SceneLighting';
import { ScenePostProcessing } from 'app/modules/wallforming/components/scenes/ScenePostProcessing';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const screenWidth = window.screen.width;

export const CatalogueArticlePreview = memo(() => {
    const { t } = useTranslation();

    const { fitToObject, isArticleInsertionEnabled, selectedArticleId } = usePlacementStore(
        useShallow((state) => ({
            fitToObject: state.fitToObject,
            isArticleInsertionEnabled: state.isArticleInsertionEnabled,
            selectedArticleId: state.selectedArticleId
        }))
    );

    const { shadowsEnabled, reflectionsEnabled } = usePlacementStore(useShallow((state) => state.graphicsSettings));

    const previewHelperRef = useRef<BoxHelper | undefined>(undefined);
    const previewCameraControlsRef = useRef<CameraControls | null>(null);
    const webGLRendererRef = useRef<WebGLRenderer | null>(null);
    
    const { restorePreviewCamera, savePreviewCamera } = usePersistentPreviewCamera(
        previewCameraControlsRef,
        'wallforming-article-preview-camera-view',
        selectedArticleId
    );

    const handleFocusArticle = () => {
        if (previewHelperRef.current && previewCameraControlsRef.current) {
            fitToObject(previewHelperRef.current, previewCameraControlsRef, true, false);
        }
    };

    const handleOnPointerEnter = () => {
        if (!webGLRendererRef.current) {
            return;
        }
        const cursorStyle = isArticleInsertionEnabled() ? 'move' : 'auto';
        webGLRendererRef.current.domElement.style.setProperty('cursor', cursorStyle);
    };

    return (
        <div className="flex flex-col h-full justify-center items-center border-b-0">
            <div
                className="h-full w-full border border-border-ultra-light rounded-none overflow-hidden focus:outline-none"
                data-article-drag-id={selectedArticleId ?? ''}
                draggable={false}
                id="previewcanvas"
                tabIndex={0}
                data-input-scene
                onContextMenu={(e) => e.preventDefault()}
                onPointerDown={(e) => {
                    e.stopPropagation();
                    e.currentTarget.focus();
                }}
                onPointerEnter={handleOnPointerEnter}
            >
                <>
                    <div className="relative">
                        <div className="absolute top-3 left-3 z-50">
                            <div
                                className="flex flex-col justify-start items-start gap-y-2"
                                data-article-drag-ignore
                                onDoubleClick={(e) => e.stopPropagation()}
                            >
                                <Tooltip
                                    label={
                                        <div className="flex items-center gap-2">
                                            <span>{t('buttonFocusArticle')}</span>
                                            <ActionShortcutHint actionId={InputActionId.CameraCenter} />
                                        </div>
                                    }
                                    position="right"
                                    variant={variants.tooltip.right}
                                >
                                    <ActionIcon
                                        className={`
                                        bg-background-gray-ultra-light hover:bg-background-gray! catalogue-preview-action-icon
                                        ${screenWidth >= 1920 ? 'action-icon-large' : 'action-icon-medium'}
                                    `}
                                        onClick={handleFocusArticle}
                                    >
                                        view_in_ar
                                    </ActionIcon>
                                </Tooltip>
                                <InsertPointsVisibiltyButton />
                            </div>
                        </div>
                    </div>
                    <Canvas
                        frameloop="demand"
                        shadows={shadowsEnabled ? 'soft' : false}
                        orthographic
                        onCreated={(state) => {
                            webGLRendererRef.current = state.gl;
                        }}
                    >
                        <PreviewCameraControls
                            previewCameraControlsRef={previewCameraControlsRef}
                            onCenterCamera={handleFocusArticle}
                            onRest={savePreviewCamera}
                        />
                        <PreviewOrientationCube />

                        <ArticlePreviewScene
                            previewCameraControlsRef={previewCameraControlsRef}
                            previewHelperRef={previewHelperRef}
                            restorePreviewCamera={restorePreviewCamera}
                        />

                        <Suspense>
                            <Environment
                                background={false}
                                environmentIntensity={shadowsEnabled || reflectionsEnabled ? 1.6 : 0}
                                environmentRotation={[Math.PI / 2, -1, 0]}
                                preset="city"
                            />
                        </Suspense>

                        <SceneLighting />
                        <ScenePostProcessing />
                    </Canvas>
                </>
            </div>
        </div>
    );
});

CatalogueArticlePreview.displayName = 'CatalogueArticlePreview';
